<template>
  <li
    class="sg-menu-item"
    role="menuitem"
    tabindex="-1"
    :style="paddingStyle"
    :class="{
      'is-active': active,
      'is-disabled': disabled,
    }"
    @click="handleClick"
  >
    <sg-tooltip
      v-if="
        parentMenu.type.name === 'SgMenu' &&
        rootMenu.props.collapse &&
        $slots.title
      "
      :theme="Effect.DARK"
      trigger='hover'
      placement="right"
      persistent
    >
      <template #content>
        <slot name="title" />
      </template>
      <div class="sg-menu-tooltip__trigger">
        <slot />
      </div>
    </sg-tooltip>
    <template v-else>
      <span v-if="rootMenu.props.mode === 'horizontal'">
        <slot />
        <slot name="title" />
      </span>
      <span v-else class="sg-menu-item-title-wrapper">
        <template v-if="tooltipText && rootMenu.props.showTooltip">
          <sg-tooltip
            :disabled="!tooltipDisabled"
            class="box-item"
            theme="dark"
            :content="tooltipText"
            placement="top"
          >
            <div ref="content" @mouseenter="handleTooltipIn" @mouseleave="handleTooltipOut">
              <span class="sg-menu-item-title"><slot /></span>
              <slot name="title" />
            </div>
          </sg-tooltip>
        </template>
        <span v-else>
          <slot></slot>
          <slot name="title" />
        </span>
      </span>
    </template>
  </li>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  inject,
  getCurrentInstance,
  toRef,
  reactive,
} from 'vue'
import SgTooltip from '@sgui-plus/components/tooltip'
import { Effect } from '@sgui-plus/components/popper'
import { throwError } from '@sgui-plus/utils/error'
import useMenu from './use-menu'
import { menuItemEmits, menuItemProps } from './menu-item'

import type { MenuItemRegistered, MenuProvider, SubMenuProvider } from './types'

const COMPONENT_NAME = 'SgMenuItem'
export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SgTooltip,
  },

  props: menuItemProps,
  emits: menuItemEmits,

  setup(props, { emit }) {
    const tooltipDisabled = ref(false)
    const instance = getCurrentInstance()! as any
    const rootMenu = inject<MenuProvider>('rootMenu')
    if (!rootMenu) throwError(COMPONENT_NAME, 'can not inject root menu')

    const { parentMenu, paddingStyle, indexPath } = useMenu(
      instance,
      toRef(props, 'index')
    )
    const handleTooltipIn = ()=>{
      tooltipDisabled.value = instance.refs.content.scrollWidth > instance.refs.content.offsetWidth
    }
    const handleTooltipOut = ()=>{
      tooltipDisabled.value  = false;
    }
    const subMenu = inject<SubMenuProvider>(`subMenu:${parentMenu.value.uid}`)
    if (!subMenu) throwError(COMPONENT_NAME, 'can not inject sub menu')

    const active = computed(() => props.index === rootMenu.activeIndex)
    const item: MenuItemRegistered = reactive({
      index: props.index,
      indexPath,
      active,
    })

    const handleClick = () => {
      if (!props.disabled) {
        rootMenu.handleMenuItemClick({
          index: props.index,
          indexPath: indexPath.value,
          route: props.route,
        })
        emit('onClick', item)
      }
    }

    onMounted(() => {
      subMenu.addSubMenu(item)
      rootMenu.addMenuItem(item)
    })

    onBeforeUnmount(() => {
      subMenu.removeSubMenu(item)
      rootMenu.removeMenuItem(item)
    })

    return {
      Effect,
      parentMenu,
      rootMenu,
      paddingStyle,
      active,
      handleTooltipIn,
      handleTooltipOut,
      tooltipDisabled,
      handleClick,
    }
  },
})
</script>
